import {ModelRow, StringUtil} from "@mcleod/core";
import { SecondaryFilterProps } from "./FilterPanel";
import { PlanningUtil, TempWarnings } from "./PlanningUtil";
import { LMEGeneralSettings } from "@mcleod/general";

export class OrderPlanningSecondaryFilter {

    static readonly FILTER_DELIVERED_ORDERS: SecondaryFilterProps = {
        id: "delOrders",
        caption: "Delivered Orders",
        filter: (row: ModelRow) => row.get("move_status", "") === "D"
    }

    static readonly FILTER_DELIVERED_DRIVERS: SecondaryFilterProps = {
        id: "delDrivers",
        caption: "Delivered Drivers",
        filter: (row: ModelRow) => this.FILTER_DELIVERED_ORDERS.filter(row)
            && row.get("driver1_preassign_count", 0) < 1
    }

    static readonly FILTER_UNASSIGNED_TRACTORS: SecondaryFilterProps = {
        id: "unaTractors",
        caption: "Unassigned Tractors",
        filter: (row: ModelRow) => row.get("driver1_preassign_count", 0) < 1
    }

    static readonly FILTER_DISPATCH_BY_UNASSIGNED: SecondaryFilterProps = {
        id: "dispatchByUnassigned",
        caption: "Unassigned",
        filter: (row: ModelRow) => row.get("dispatchby_preassign_count") < 1
    }

    static readonly FILTER_DISPATCH_BY_ASSIGNED: SecondaryFilterProps = {
        id: "dispatchByAssigned",
        caption: "Assigned",
        filter: (row: ModelRow) => !this.FILTER_DISPATCH_BY_UNASSIGNED.filter(row)
    }

    static readonly FILTER_UNASSIGNED: SecondaryFilterProps = {
        id: "unassigned",
        caption: "Unassigned",
        filter: (row: ModelRow) => {
            const dispatchBy:"D"|"T" = LMEGeneralSettings.getSingleton().getDispatchControl().dispatch_by;
            return row.isNull("D" === dispatchBy ? "driver1_id" : "tractor")
        }
    }

    static readonly FILTER_ASSIGNED: SecondaryFilterProps = {
        id: "assigned",
        caption: "Assigned",
        filter: (row: ModelRow) => !this.FILTER_UNASSIGNED.filter(row)
    }

    static readonly FILTER_EMPTIED: SecondaryFilterProps = {
        id: "empty",
        caption: "Empty",
        filter: (row: ModelRow) => row.getBoolean("emptied", false)
    }

    static readonly FILTER_LOADED: SecondaryFilterProps = {
        id: "loaded",
        caption: "Loaded",
        filter: (row: ModelRow) => !this.FILTER_EMPTIED.filter(row)
    }

    static readonly FILTER_APPT_REQUIRED: SecondaryFilterProps = {
        id: "apptRequired",
        caption: "Appointment Required",
        filter: (row: ModelRow) => (
            (row.getBoolean("origin_appt_required", false)
                && !row.getBoolean("origin_appt_confirmed", false)
                && row.isNull("origin_actual_arrival"))
            || (row.getBoolean("destination_appt_required", false)
                && !row.getBoolean("destination_appt_confirmed", false)
                && row.isNull("destination_actual_arrival"))
            || row.getNumber("appt_required_count", 0) > 0
        )
    }

    static readonly FILTER_APPT_CONFIRMED: SecondaryFilterProps = {
        id: "apptConfirmed",
        caption: "Appointment Confirmed",
        filter: (row: ModelRow) => (
            (row.getBoolean("origin_appt_required", false)
                && row.getBoolean("origin_appt_confirmed", false)
                && row.isNull("origin_actual_arrival"))
            || (row.getBoolean("destination_appt_required", false)
                && row.getBoolean("destination_appt_confirmed", false)
                && row.isNull("destination_actual_arrival"))
            || row.getNumber("appt_confirmed_count", 0) > 0
        )
    }

    static readonly FILTER_LATE: SecondaryFilterProps = {
        id: "isLate",
        caption: "Appointment Late",
        filter: (row: ModelRow) => {
            return (PlanningUtil.isLateWarning(row, "origin")
                || PlanningUtil.isLateWarning(row, "destination"));
        }
    }

    static readonly FILTER_SHIPMENT_STATUS_ERRORS: SecondaryFilterProps = {
        id: "shipmentStatusError",
        caption: "Shipment Status Error",
        licensedPackage: "com.tms.client.loadmaster.edi",
        filter: (row: ModelRow) =>
            row.getBoolean("has_shipment_error", false)

    }

    static readonly FILTER_ARRIVED_AT_SHIPPER = {
        id: "arrivedAtShipper",
        caption: "Arrived At Shipper",
        filter: (row: ModelRow) => this.arrivedAtShipper(row)
    };

    static readonly FILTER_EQUIP_ARRIVED_AT_SHIPPER = {
        id: "arrivedAtEquipShipper",
        caption: "Arrived At Shipper",
        filter: (row: ModelRow) => {
            const moveStatus = row.getString("move_status", null);
            if (StringUtil.isEmptyString(moveStatus)) return false;

            switch (moveStatus) {
                case "D":
                case "A":
                    return this.arrivedAtPreassignment(row);
                case "P":
                    return this.arrivedAtShipper(row);
                default:
                    return false;
            }
        }
    };

    private static arrivedAtPreassignment(row: ModelRow) {
        // For delivered moves, the next stop is going to be the first stop on the next pre-assignment
        return row.getString("np_origin_stop_status") === "A"
        && !row.isNull("np_origin_actual_arrival")
        && row.isNull("np_origin_actual_departure");
    }

    private static arrivedAtShipper(row: ModelRow) {
        return row.getBoolean("origin_stop_available", true)
        && !row.isNull("origin_actual_arrival")
        && row.isNull("origin_actual_departure")
    }

    static readonly FILTER_ARRIVED_AT_CONSIGNEE = {
        id: "arrivedAtConsignee",
        caption: "Arrived At Consignee",
        filter: (row: ModelRow) => {
            return !row.isNull("destination_actual_arrival")
            && row.isNull("destination_actual_departure")
            && row.getString("destination_stop_type", null) === "SO"
        },
    }

    static readonly FILTER_TEMP_WARNINGS: SecondaryFilterProps = {
        id: "trailerTempWarnings",
        caption: "Trailer Temp Warnings",
        filter: (row: ModelRow) => {
            const result = PlanningUtil.getTempWarnings(row);
            return (result.return_air != null && result.return_air == TempWarnings.EXCEEDS)
                || (result.setpoint != null && result.setpoint == TempWarnings.EXCEEDS);
        },
        visible: (data: any) => {
            if (Array.isArray(data)) {
                return data.some(row => {
                    if (row instanceof ModelRow) {
                        const result = PlanningUtil.getTempWarnings(row);
                        return (result.return_air != null && result.return_air == TempWarnings.EXCEEDS)
                            || (result.setpoint != null && result.setpoint == TempWarnings.EXCEEDS);
                    }
                    return false;
                });
            }
            return true;
        }
    }

    static readonly FILTER_TRACKING_LATE = {
        id: "trackingLate",
        caption: "Tracking Late",
        filter: (row: ModelRow) => {
            return ["A", "C", "P"].includes(row.getString("move_status")) && this.isTrackingLate(row)
        }
    }

    private static isTrackingLate(row: ModelRow): boolean {
        if (row.get(`next_stop_actual_arrival`) != null) return false;

        const stopTime: Date = new Date(PlanningUtil.getStopTime(row, "next_stop"));
        const etaToNextStopTimeZone: string = row.get("next_stop_timezone") != null ? " " + row.get("next_stop_timezone") : "";
        const etaToNextStop: Date = new Date(row.getDateTime("next_stop_eta_time") + etaToNextStopTimeZone);
        const diff = etaToNextStop != null ? etaToNextStop.getTime() - stopTime.getTime() : null;
        if (diff == null) return false;

        return diff > 0;
    }

    static readonly FILTER_UNSEATED_TRACTORS: SecondaryFilterProps = {
        id: "unseatedTractors",
        caption: "Unseated Tractors",
        filter: (row: ModelRow) => {
            return row.getBoolean("tractor_unseated", false);
        }
    }

    static readonly FILTER_MC_MESSAGES: SecondaryFilterProps = {
        id: "mcMessages",
        caption: "MC Messages",
        filter: (row: ModelRow) => {
            return row.get("mc_message_count", 0) > 0;
        }
    }

    static readonly FILTER_FREE_TIME_STARTED = {
        id: "freeTimeStarted",
        caption: "Free Time Started",
        licensedPackage: "com.tms.client.loadmaster.det",
        filter: (row: ModelRow) =>
            "A" !== row.getString("move_status", "")
            && ("F" === row.getString("origin_detention", "")
                || "F" === row.getString("destination_detention", "")
                || "F" === row.getString("next_stop_detention", "")
                || row.get("stop_detention_statuses", []).includes("F"))
    }

    static readonly FILTER_DETENTION_CHARGING = {
        id: "detentionCharging",
        caption: "Detention Charging",
        licensedPackage: "com.tms.client.loadmaster.det",
        filter: (row: ModelRow) =>
            "A" !== row.getString("move_status", "")
            && ("B" === row.getString("origin_detention", "")
                || "B" === row.getString("destination_detention", "")
                || "B" === row.getString("next_stop_detention", "")
                || row.get("stop_detention_statuses", []).includes("B"))
    }

    static readonly FILTER_DETENTION_OCCURRED = {
        id: "detentionOccurred",
        caption: "Detention Occurred",
        licensedPackage: "com.tms.client.loadmaster.det",
        filter: (row: ModelRow) =>
            "A" !== row.getString("move_status", "")
            && ("D" === row.getString("origin_detention", "")
                || "D" === row.getString("destination_detention", "")
                || "D" === row.getString("next_stop_detention", "")
                || row.get("stop_detention_statuses", []).includes("D"))
    }

    static readonly FILTER_DETENTION_CLOSED = {
        id: "detentionClosed",
        caption: "Detention Closed",
        licensedPackage: "com.tms.client.loadmaster.det",
        filter: (row: ModelRow) =>
            "A" !== row.getString("move_status", "")
            && ("C" === row.getString("origin_detention", "")
                || "C" === row.getString("destination_detention", "")
                || "C" === row.getString("next_stop_detention", "")
                || row.get("stop_detention_statuses", []).includes("C"))
    }
}
